import React, { useEffect, useState } from 'react';
import { User, Mail, Lock, Building, MapPin, Save, Eye, EyeOff, ChevronDown, ChevronUp, Wallet, TrendingDown, Calendar } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { supabaseClient } from '../lib/supabase';
import { getCompanies } from '../utils/companyManagement';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { useBalance, formatMonth } from '../contexts/BalanceContext';
export function EmployeeProfilePage() {
  useDocumentTitle('Mi Perfil');
  const {
    user
  } = useAuth();
  const {
    currentBalance,
    loading: balanceLoading,
    hasBalance
  } = useBalance();
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [employeeData, setEmployeeData] = useState<any>(null);
  const [companyData, setCompanyData] = useState<any>(null);
  useEffect(() => {
    loadEmployeeData();
  }, [user]);
  const loadEmployeeData = async () => {
    if (!user?.employeeId) return;
    setLoading(true);
    try {
      const employees = await supabaseClient.getEmployees();
      const employee = employees.find((e: any) => e.id === user.employeeId);
      if (employee) {
        setEmployeeData(employee);
        setFormData({
          name: employee.name || '',
          email: employee.email || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // Load company data to check subsidyEnabled
        try {
          const allCompanies = await supabaseClient.getCompanies();
          const empCompany = allCompanies.find((c: any) => c.id === employee.company_id);
          setCompanyData(empCompany || null);
        } catch (e) {
          // Fallback to localStorage
          const saved = localStorage.getItem('criket_companies');
          if (saved) {
            const allCompanies = JSON.parse(saved);
            const empCompany = allCompanies.find((c: any) => c.id === employee.company_id);
            setCompanyData(empCompany || null);
          }
        }
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      showToast('error', 'Error', 'No se pudo cargar la información');
    } finally {
      setLoading(false);
    }
  };
  const handleSave = async () => {
    // Validations
    if (!formData.name.trim()) {
      showToast('warning', 'Campo requerido', 'El nombre es obligatorio');
      return;
    }
    if (!formData.email.trim()) {
      showToast('warning', 'Campo requerido', 'El email es obligatorio');
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast('warning', 'Email inválido', 'Por favor ingresa un email válido');
      return;
    }
    // Password change validation
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        showToast('warning', 'Contraseña actual requerida', 'Debes ingresar tu contraseña actual para cambiarla');
        return;
      }
      if (formData.currentPassword !== employeeData.password) {
        showToast('error', 'Contraseña incorrecta', 'La contraseña actual no es correcta');
        return;
      }
      if (formData.newPassword.length < 6) {
        showToast('warning', 'Contraseña muy corta', 'La nueva contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        showToast('error', 'Contraseñas no coinciden', 'La nueva contraseña y la confirmación deben ser iguales');
        return;
      }
    }
    setSaving(true);
    try {
      const updateData: any = {
        email: formData.email.trim()
      };
      // Only update password if new password is provided
      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }
      await supabaseClient.updateEmployee(user!.employeeId!, updateData);
      showToast('success', 'Perfil actualizado', 'Tu información ha sido actualizada correctamente');
      // Clear password fields
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Reload data
      await loadEmployeeData();
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('error', 'Error', 'No se pudo actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };
  const companies = getCompanies();
  const company = companyData || companies.find(c => c.id === employeeData?.company_id);
  const branch = company?.branches?.find((b: any) => b.id === employeeData?.branch_id);
  const subsidyEnabled = company?.subsidyEnabled === true;
  if (loading) {
    return <div className="flex items-center justify-center py-20" data-id="element-3310">
        <div className="text-center" data-id="element-3311">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-black dark:border-white mx-auto mb-3" data-id="element-3312"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-3313">
            Cargando perfil...
          </p>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-lg mx-auto space-y-4" data-id="element-3314">
      {/* Profile Card */}
      <Card noPadding data-id="element-3315">
        {/* Name + position header — no avatar */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800" data-id="element-3316">
          <p className="font-bold text-gray-900 dark:text-white text-xl leading-tight truncate" data-id="element-3317">
            {formData.name}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-3318">
            {employeeData?.position || 'Empleado'}
          </p>
        </div>

        {/* Company + Branch */}
        <div className="flex items-center gap-4 px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-100 dark:border-gray-800 text-xs text-gray-600 dark:text-gray-400" data-id="element-3319">
          <span className="flex items-center gap-1.5" data-id="element-3320">
            <Building size={13} className="text-gray-400 shrink-0" data-id="element-3321" />
            <span className="font-medium text-gray-800 dark:text-gray-200" data-id="element-3322">
              {company?.name || 'N/A'}
            </span>
          </span>
          <span className="text-gray-300 dark:text-gray-700" data-id="element-3323">·</span>
          <span className="flex items-center gap-1.5" data-id="element-3324">
            <MapPin size={13} className="text-gray-400 shrink-0" data-id="element-3325" />
            <span className="font-medium text-gray-800 dark:text-gray-200" data-id="element-3326">
              {branch?.name || 'N/A'}
            </span>
          </span>
        </div>

        {/* Email field */}
        <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-800" data-id="element-3327">
          <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2" data-id="element-3328">
            Email
          </label>
          <div className="relative" data-id="element-3329">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} data-id="element-3330" />
            <input type="email" value={formData.email} onChange={e => setFormData({
            ...formData,
            email: e.target.value
          })} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="tu@email.com" data-id="element-3331" />
          </div>
        </div>

        {/* Password section — collapsible */}
        <div className="border-b border-gray-100 dark:border-gray-800" data-id="element-3332">
          <button onClick={() => setShowPasswordSection(!showPasswordSection)} className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-3333">
            <span className="flex items-center gap-2" data-id="element-3334">
              <Lock size={15} className="text-gray-400" data-id="element-3335" />
              Cambiar Contraseña
            </span>
            {showPasswordSection ? <ChevronUp size={16} className="text-gray-400" data-id="element-3336" /> : <ChevronDown size={16} className="text-gray-400" data-id="element-3337" />}
          </button>

          {showPasswordSection && <div className="px-5 pb-4 space-y-3" data-id="element-3338">
              <div className="relative" data-id="element-3339">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} data-id="element-3340" />
                <input type={showPassword ? 'text' : 'password'} value={formData.currentPassword} onChange={e => setFormData({
              ...formData,
              currentPassword: e.target.value
            })} className="w-full pl-9 pr-10 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="Contraseña actual" data-id="element-3341" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3342">
                  {showPassword ? <EyeOff size={15} data-id="element-3343" /> : <Eye size={15} data-id="element-3344" />}
                </button>
              </div>
              <div className="relative" data-id="element-3345">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} data-id="element-3346" />
                <input type={showNewPassword ? 'text' : 'password'} value={formData.newPassword} onChange={e => setFormData({
              ...formData,
              newPassword: e.target.value
            })} className="w-full pl-9 pr-10 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="Nueva contraseña (mín. 6 caracteres)" data-id="element-3347" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-3348">
                  {showNewPassword ? <EyeOff size={15} data-id="element-3349" /> : <Eye size={15} data-id="element-3350" />}
                </button>
              </div>
              <div className="relative" data-id="element-3351">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={15} data-id="element-3352" />
                <input type={showNewPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            })} className="w-full pl-9 pr-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" placeholder="Confirmar nueva contraseña" data-id="element-3353" />
              </div>
            </div>}
        </div>

        {/* Save button */}
        <div className="px-5 py-4" data-id="element-3354">
          <Button onClick={handleSave} disabled={saving} leftIcon={saving ? undefined : <Save size={15} data-id="element-3356" />} fullWidth data-id="element-3355">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </Card>

      {/* Balance Card — shown when company has subsidy enabled */}
      {subsidyEnabled && !balanceLoading && <Card noPadding data-id="element-3357">
          {/* Accent bar */}
          <div className="h-1 bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-400 rounded-t-xl" data-id="element-3358" />
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100 dark:border-gray-800" data-id="element-3359">
            <div className="flex items-center gap-2" data-id="element-3360">
              <div className="p-1.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg" data-id="element-3361">
                <Wallet size={15} className="text-emerald-600 dark:text-emerald-400" data-id="element-3362" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white" data-id="element-3363">
                Mi Balance del Mes
              </h3>
            </div>
            {currentBalance && <span className="flex items-center gap-1 text-[11px] text-gray-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1 rounded-full font-semibold" data-id="element-3364">
                <Calendar size={10} data-id="element-3365" />
                {formatMonth(currentBalance.month, currentBalance.year)}
              </span>}
          </div>

          {currentBalance ? <>
              <div className="grid grid-cols-3 divide-x divide-gray-100 dark:divide-gray-800" data-id="element-3366">
                <div className="px-4 py-4" data-id="element-3367">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5" data-id="element-3368">
                    Disponible
                  </p>
                  <p className={`text-sm font-bold font-mono tabular-nums whitespace-nowrap ${currentBalance.currentBalance > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`} data-id="element-3369">
                    RD$ {currentBalance.currentBalance.toFixed(2)}
                  </p>
                  <div className="mt-2 w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1.5 overflow-hidden" data-id="element-3370">
                    <div className="bg-emerald-500 h-full rounded-full transition-all duration-700" style={{
                width: `${Math.min(100, currentBalance.currentBalance / currentBalance.assignedAmount * 100)}%`
              }} data-id="element-3371" />
                  </div>
                </div>
                <div className="px-4 py-4" data-id="element-3372">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5" data-id="element-3373">
                    Asignado
                  </p>
                  <p className="text-sm font-bold font-mono tabular-nums whitespace-nowrap text-gray-900 dark:text-white" data-id="element-3374">
                    RD$ {currentBalance.assignedAmount.toFixed(2)}
                  </p>
                </div>
                <div className="px-4 py-4" data-id="element-3375">
                  <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1.5" data-id="element-3376">
                    Consumido
                  </p>
                  <p className="text-sm font-bold font-mono tabular-nums whitespace-nowrap text-orange-600 dark:text-orange-400" data-id="element-3377">
                    RD${' '}
                    {(currentBalance.assignedAmount - currentBalance.currentBalance).toFixed(2)}
                  </p>
                </div>
              </div>
              {currentBalance.notes && <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50" data-id="element-3378">
                  {currentBalance.notes}
                </div>}
            </> : <div className="px-5 py-6 text-center" data-id="element-3379">
              <div className="w-10 h-10 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-3380">
                <Wallet size={18} className="text-gray-400" data-id="element-3381" />
              </div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400" data-id="element-3382">
                Sin balance asignado este mes
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5" data-id="element-3383">
                Contacta a tu gerente para solicitar tu subsidio
              </p>
            </div>}
        </Card>}
    </div>;
}